import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, VehicleType, type VehicleHazard } from '../../../hazards/HazardDefinition';
import { hazardSocket } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, moduleSides, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';
import { mixedLanes } from './SEG_CRANE_SWING_RUN';

/**
 * SEG_CABLE_REEL_RUN_60M – three big cable drums rolling at the runner close behind each other (user 2026-10-05 late: "sie
 * können wirklich sehr knapp hintereinander losrollen, dass man hier wirklich durch muss … nicht … drei Sekunden Zeit zur
 * nächsten. Es soll schon ein wenig Stress verursachen und es muss so gerade halt schaffbar sein").
 *
 * The drums meet the runner 17 m apart (≈ 1.06 s at top speed 16 m/s – the validator wants ≈ 0.78 s for reaction and two lane
 * changes), each in its own lane, the three lanes in a shuffled order: every drum closes the lane that was open for the one
 * before, there is no lane to stay in. Each drum waits 14 m before its meeting line, so the next one already rolls while the
 * runner dodges the first.
 */
const W = WORLD_CONFIG;
const LENGTH = 60;
const MEETS = [10, 27, 44] as const;
const ROLL = 14;
const VARIANTS = 6;
const SOCKET = (k: number) => `SOCKET_EVT_CABLE_REEL_RUN_${k + 1}`;

const drum = (k: number, lane: number): VehicleHazard => ({
  id: `HAZ_REEL_RUN_${k + 1}_L${lane}`,
  kind: HazardKind.Vehicle,
  vehicle: VehicleType.CableReel,
  socketId: SOCKET(k),
  lane,
  startZ: MEETS[k]! + ROLL,
  meetZ: MEETS[k]!,
  stopZ: Math.max(2, MEETS[k]! - 8),
});

/** Small deterministic generator for the fixed layouts. */
function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

const random = lcg(4711);
const VARIANT_LIST: ObstacleVariant[] = Array.from({ length: VARIANTS }, (_, v) => ({
  id: `REEL_RUN_${v + 1}`,
  weight: 1,
  placements: [],
  hazards: mixedLanes(MEETS.length, random).map((lane, k) => drum(k, lane).id),
}));
const SIDES = moduleSides(LENGTH);

export const SEG_CABLE_REEL_RUN_60M: SegmentDefinition = {
  id: 'SEG_CABLE_REEL_RUN_60M',
  kind: SegmentKind.Straight,
  displayName: 'Kabeltrommeln dicht 60 m',
  length: LENGTH,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [20, 40].map((z) => ({ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z } })),
  entry: entryConnector(),
  exits: [straightExit(LENGTH)],
  lanes: standardLanes(W),
  obstacleSpawnPoints: [],
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: VARIANT_LIST,
  hazards: MEETS.flatMap((_, k) => [0, 1, 2].map((lane) => drum(k, lane))),
  collectibleSpawnPoints: collectibleLanes(W, 1, LENGTH - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),
  ...SIDES,
  dynamicEventSockets: [...SIDES.dynamicEventSockets, ...MEETS.map((z, k) => hazardSocket(SOCKET(k), 0, z, DynamicEventType.ForkliftCrossing))],
  generation: {
    weight: 0.45,
    minDistance: 700,
    minSegmentsAfterJunction: 1,
    // the same family as the single drums: a dense module never right behind a drum series, and the other way round
    family: 'CABLE_REEL',
    chain: {
      lengths: [[1, 0.6], [2, 0.4]],
      from: [{ distance: 2500, lengths: [[1, 0.35], [2, 0.45], [3, 0.2]] }],
    },
    tags: ['straight', 'hazard', 'vehicle', 'cable'],
  },
};
